/**
 * useAdminLang — dedicated 4-language selector for the Admin Space.
 * Independent from the app-wide user language. Persisted in localStorage.
 * Supported: fr, en, es, zh.
 */
import { useCallback, useEffect, useState } from 'react';
import { adminI18n, adminSupportedLanguages } from '../utils/adminTranslations';

const STORAGE_KEY = 'destine_admin_lang';

const readInitial = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && adminSupportedLanguages.includes(saved)) return saved;
  } catch { /* ignore */ }
  return 'fr';
};

let listeners = new Set();
let currentLang = readInitial();

const notify = () => listeners.forEach(fn => fn(currentLang));

export const useAdminLang = () => {
  const [lang, setLangState] = useState(currentLang);

  useEffect(() => {
    const l = (v) => setLangState(v);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);

  const setAdminLang = useCallback((next) => {
    if (!adminSupportedLanguages.includes(next)) return;
    currentLang = next;
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    notify();
  }, []);

  const at = useCallback((key, params = null) => {
    const dict = adminI18n[lang] || adminI18n.fr;
    let v = dict[key];
    if (v === undefined) v = adminI18n.en?.[key] ?? key;
    if (params && typeof v === 'string') {
      for (const p of Object.keys(params)) {
        v = v.replace(`{${p}}`, params[p]);
      }
    }
    return v;
  }, [lang]);

  return { adminLang: lang, setAdminLang, at, supported: adminSupportedLanguages };
};
